import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Video from "core/components/Video";
import { useViewer } from "core/components/ViewerProvider";
import { useMutation } from "@apollo/client";
import { CreateViewMutation } from "core/mutations";
import { createEvent } from "core/utils/tracker";
import { postMessage } from "core/utils/tracker";
import getClientIp from "core/utils/getClientIp";
import { getUtmFromLocalStorage } from "core/utils/utm";
import ReactGA from "react-ga4";
import ShortyVideoContent from "./ShortyVideoContent";
import apm from "core/utils/apm";

const ShortyPlayer = React.forwardRef(function ShortyPlayer(
  {
    closeRouteDialogButton,
    muted = false, // По умолчанию muted для автоплея
    autoPlay = true, // Всегда автоплей
    preload = false, // Предзагрузка без старта
    shouldStart = true, // Нужно ли стартовать видео
    content,
    contentId, // eslint-disable-line no-unused-vars
    productId, // eslint-disable-line no-unused-vars
    collectionId, // eslint-disable-line no-unused-vars
    start,
    finish,
    showNextPreview = true, // eslint-disable-line no-unused-vars
    onPrevious,
    onNext,
    canGoPrevious,
    canGoNext,
    onVideoReady, // Callback когда видео готово к воспроизведению
    onVideoActionsReady, // Callback для передачи videoActions родителю
  },
  ref
) {
  const viewer = useViewer();
  const [trackedEvents, setTrackedEvents] = useState([]);

  const refetchQueries = [];
  if (content?.premium) {
    refetchQueries.push("Viewer");
  }

  const [createView, createViewResult] = useMutation(CreateViewMutation, {
    refetchQueries,
    onCompleted() {
      createEvent(content.type.toLowerCase(), "start");
      ReactGA.event({
        category: "User",
        action: "Create view",
        label: content.title,
      });
    },
  });

  // Автоматически создаем просмотр при монтировании компонента (для автоплея)
  useEffect(() => {
    if (
      content?.access?.token &&
      !createViewResult.data &&
      !createViewResult.loading
    ) {
      const variables = {
        token: content.access.token,
        utm: getUtmFromLocalStorage(),
      };

      getClientIp(content.getIpUrl)
        .then((ip) => {
          if (ip) {
            variables.clientIp = ip;
          }

          return createView({
            variables,
          });
        })
        .catch((error) => {
          apm.captureError(error);
          console.error("create view error", error);

          // setError(error);
        });
    }
  }, [content, createView, createViewResult.data, createViewResult.loading]);

  // Обработчики событий видео
  const handlePlay = () => {
    if (!trackedEvents.includes("start")) {
      createEvent("video", "start");
      setTrackedEvents((prev) => [...prev, "start"]);
      postMessage({
        eventName: "START",
        contentType: "video",
      });
    }
  };

  const handleUpdate = ({ currentTime, duration }) => {
    const percent = (100 / duration) * currentTime;

    if (percent > 75 && !trackedEvents.includes("thirdQuartile")) {
      createEvent("video", "thirdQuartile");
      setTrackedEvents((prev) => [...prev, "thirdQuartile"]);
      postMessage({
        eventName: "THIRDQUARTILE",
        contentType: "video",
      });
    } else if (percent > 50 && !trackedEvents.includes("midpoint")) {
      createEvent("video", "midpoint");
      setTrackedEvents((prev) => [...prev, "midpoint"]);
      postMessage({
        eventName: "MIDPOINT",
        contentType: "video",
      });
    } else if (percent > 25 && !trackedEvents.includes("firstQuartile")) {
      createEvent("video", "firstQuartile");
      setTrackedEvents((prev) => [...prev, "firstQuartile"]);
      postMessage({
        eventName: "FIRSTQUARTILE",
        contentType: "video",
      });
    }
  };

  const handleEnded = (vid) => {
    if (!trackedEvents.includes("end")) {
      createEvent("video", "end");
      setTrackedEvents((prev) => [...prev, "end"]);
      postMessage({
        eventName: "END",
        contentType: "video",
      });
    }

    // Автоматическое зацикливание для Shorty (как в YouTube Shorts)
    // Перезапускаем видео сразу после окончания
    if (vid && vid.play) {
      vid.play().catch(console.error);
    }
  };

  const view = createViewResult?.data?.createView;
  const video = view?.video;

  // Показываем пустой контейнер если нет контента
  if (!content) {
    return null;
  }

  return (
    <Video
      key={content.id}
      ref={ref}
      hlsUrl={video?.playlist || null}
      drm={video?.drmPlayerSettings}
      autoPlay={preload ? false : autoPlay} // При предзагрузке не автоплеим
      muted={muted}
      loop={true} // Зацикливание как в YouTube Shorts
      coverFitMode={true} // Включаем cover fit для полноэкранного видео
      start={start}
      finish={finish}
      mediaId={video?.id}
      viewId={view?.id}
      content={content}
      updateVideoPosition={!!viewer}
      onPlay={handlePlay}
      onUpdate={handleUpdate}
      onEnded={handleEnded}
      onCanPlay={onVideoReady} // Callback когда видео готово к воспроизведению
      controls={false} // Убираем встроенные контролы
    >
      {(videoElement, videoState, videoActions, videoRef) => {
        // Показываем превью пока видео не готово
        const isVideoReady =
          video &&
          !createViewResult.loading &&
          !videoState.isLoading &&
          videoState.status !== "loading";

        return (
          <ShortyVideoContent
            videoElement={videoElement}
            videoState={videoState}
            videoActions={videoActions}
            videoRef={videoRef}
            content={content}
            closeRouteDialogButton={closeRouteDialogButton}
            isVideoReady={isVideoReady}
            showThumbnail={!isVideoReady}
            onPrevious={onPrevious}
            onNext={onNext}
            canGoPrevious={canGoPrevious}
            canGoNext={canGoNext}
            preload={preload}
            shouldStart={shouldStart}
            onVideoActionsReady={onVideoActionsReady}
          />
        );
      }}
    </Video>
  );
});

export default ShortyPlayer;

ShortyPlayer.propTypes = {
  closeRouteDialogButton: PropTypes.node,
  muted: PropTypes.bool,
  autoPlay: PropTypes.bool,
  content: PropTypes.shape({
    id: PropTypes.string.isRequired,
    hideShare: PropTypes.bool,
    premium: PropTypes.bool,
    viewerPurchase: PropTypes.object,
    title: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    getIpUrl: PropTypes.string,
    access: PropTypes.shape({
      token: PropTypes.string,
    }),
    sport: PropTypes.shape({
      id: PropTypes.string.isRequired,
      slug: PropTypes.string,
    }),
  }),
  contentId: PropTypes.string.isRequired,
  productId: PropTypes.string,
  collectionId: PropTypes.string,
  start: PropTypes.number,
  finish: PropTypes.number,
  showNextPreview: PropTypes.bool,
  onPrevious: PropTypes.func,
  onNext: PropTypes.func,
  canGoPrevious: PropTypes.bool,
  canGoNext: PropTypes.bool,
  onVideoReady: PropTypes.func,
  onVideoActionsReady: PropTypes.func,
  preload: PropTypes.bool,
  shouldStart: PropTypes.bool,
};
